import {
  evaluateStateAt,
  dateTimeToDateTimeRecord,
  jsDateToDateTime,
  dateTimeRecordToLocalisedDateTime,
  DateTimeRecord,
} from "@visioni/schedules";
import type { AlarmSchedule, AlarmFeatureName } from "@visioni/a3s-alarm-schedules";
import { AlarmException, AlarmOverride, AlarmResponse } from "@src/services/api/types";
import { AlarmConfiguration } from "@visioni/a3s-alarm-schedules/schema";

export type CameraArmingType =
  | "schedule_armed"
  | "schedule_disarmed"
  | "override_armed"
  | "override_disarmed"
  | "exception_armed"
  | "exception_disarmed";

/**
 * Check if a camera is pristine (brand new with no configuration).
 * A camera is considered pristine when it has no timezone and no schedule configured.
 */
export const isPristine = (alarmDetail: AlarmConfiguration | null | undefined): boolean => {
  if (!alarmDetail) return true;

  const hasTimezone = !!alarmDetail.settings?.timezone;
  const hasSchedule = !!alarmDetail.configured?.schedule;

  return !hasTimezone && !hasSchedule;
};

/**
 * Extract schedule from alarmDetail, checking applied schedule first, then configured.
 */
export const getScheduleFromAlarmDetail = (alarmDetail: AlarmConfiguration): AlarmSchedule | null => {
  const appliedSchedule = alarmDetail.applied === "configured" ? alarmDetail.configured : alarmDetail.applied;
  return appliedSchedule?.schedule || alarmDetail.configured?.schedule || null;
};

/**
 * Get timezone from alarmDetail's schedule metadata.
 * Returns null if schedule is not available.
 */
export const getTimezoneFromAlarmDetail = (alarmDetail: AlarmConfiguration | null | undefined): string | null => {
  if (!alarmDetail) return null;
  const schedule = getScheduleFromAlarmDetail(alarmDetail);
  // Prefer timezone stored on schedule metadata, fall back to the alarm settings timezone
  return schedule?.metadata.timezone || alarmDetail.settings?.timezone || null;
};

export type CameraArmedState = {
  armingType: CameraArmingType;
  isInException: boolean;
  isInOverride: boolean;
  activeException: AlarmException | null;
  activeOverride: AlarmOverride | null;
  upcomingException: AlarmException | null;
};

const isDateTimeInExceptionPeriod = (dt: Date, exception: AlarmException, timezone: string): boolean => {
  const queryDate = jsDateToDateTime(dt).setZone(timezone);
  const startDate = dateTimeRecordToLocalisedDateTime(exception.period.start, timezone);
  const endDate = dateTimeRecordToLocalisedDateTime(exception.period.end, timezone);

  return queryDate >= startDate && queryDate < endDate;
};

const isDateTimeInOverridePeriod = (dt: Date, override: AlarmOverride, timezone: string): boolean => {
  const queryDate = jsDateToDateTime(dt).setZone(timezone);
  const startDate = dateTimeRecordToLocalisedDateTime(override.period.start, timezone);

  if (queryDate < startDate) {
    return false;
  }

  if (override.period.end) {
    const endDate = dateTimeRecordToLocalisedDateTime(override.period.end, timezone);
    return queryDate < endDate;
  }

  return true;
};

const responseToggleToFeatureState = (toggle: "on" | "off"): "armed" | "disarmed" => {
  return toggle === "on" ? "armed" : "disarmed";
};

const dateTimeRecordEquals = (a: DateTimeRecord | null | undefined, b: DateTimeRecord | null | undefined): boolean => {
  if (!a || !b) {
    return false;
  }

  return a.year === b.year && a.month === b.month && a.day === b.day && a.hour === b.hour && a.minute === b.minute;
};

/**
 * Check if the camera is armed based on the response config
 * The camera is considered armed only if all features in the response match
 * their expected states in the schedule.
 */
export const isCameraArmedByResponse = (
  schedule: AlarmSchedule,
  response: AlarmResponse,
  nowRecord: DateTimeRecord,
): boolean => {
  for (const [featureName, expectedToggle] of Object.entries(response)) {
    if (!(featureName in schedule.features)) {
      continue;
    }

    const currentState = evaluateStateAt(schedule, featureName as AlarmFeatureName, nowRecord);
    const expectedState = responseToggleToFeatureState(expectedToggle);

    // If any feature doesn't match, camera is not armed
    if (currentState !== expectedState) {
      return false;
    }
  }

  return true;
};

/**
 * Get the current camera armed state based on schedule, exceptions, and override
 */
export const getCurrentCameraArmedState = (
  alarmDetail: AlarmConfiguration | null,
  now: Date = new Date(),
): CameraArmedState | null => {
  if (!alarmDetail?.settings) {
    return null;
  }

  const { settings } = alarmDetail;
  const timezone = settings.timezone || "UTC";

  // Convert now to the camera's timezone
  const nowDateTime = jsDateToDateTime(now).setZone(timezone);
  const nowRecord = dateTimeToDateTimeRecord(nowDateTime);

  const activeOverride = settings.override && isDateTimeInOverridePeriod(now, settings.override, timezone);
  const activeException = settings.exceptions.find((ex) => isDateTimeInExceptionPeriod(now, ex, timezone)) || null;

  const state = {
    isInException: !!activeException,
    isInOverride: !!activeOverride,
    activeException: activeException || null,
    activeOverride: activeOverride ? settings.override : null,
    upcomingException: getNextUpcomingException(settings.exceptions, now, timezone),
  };

  // Check for active override or exception
  if (settings.override && activeOverride) {
    const isArmed = settings.override.action === "armed";
    return {
      armingType: isArmed ? "override_armed" : "override_disarmed",
      ...state,
    };
  } else if (activeException) {
    const isArmed = activeException.action === "armed";
    return {
      armingType: isArmed ? "exception_armed" : "exception_disarmed",
      ...state,
    };
  }

  // 3. Evaluate Schedule Rules
  const schedule = getScheduleFromAlarmDetail(alarmDetail);

  if (!schedule) {
    return {
      armingType: "schedule_disarmed",
      ...state,
    };
  }

  try {
    // Check if camera is armed by evaluating all features in the response
    // Camera is armed only if all response features match their expected states
    const isArmed = settings.response ? isCameraArmedByResponse(schedule, settings.response, nowRecord) : false;

    return {
      armingType: isArmed ? "schedule_armed" : "schedule_disarmed",
      ...state,
    };
  } catch (error) {
    console.warn("Failed to evaluate schedule state:", error);
    return {
      armingType: "schedule_disarmed",
      ...state,
    };
  }
};

/**
 * Get the next upcoming exception (starts after now, within the next week)
 */
export const getNextUpcomingException = (
  exceptions: AlarmException[],
  now: Date,
  timezone: string,
): AlarmException | null => {
  const nowDateTime = jsDateToDateTime(now).setZone(timezone);
  const weekFromNow = nowDateTime.plus({ days: 7 });

  // Filter for exceptions that start in the future and within the next week
  const upcoming = exceptions.filter((ex) => {
    const startDateTime = dateTimeRecordToLocalisedDateTime(ex.period.start, timezone);
    return startDateTime > nowDateTime && startDateTime <= weekFromNow;
  });

  // Sort by start time ascending
  upcoming.sort((a, b) => {
    const startA = dateTimeRecordToLocalisedDateTime(a.period.start, timezone);
    const startB = dateTimeRecordToLocalisedDateTime(b.period.start, timezone);
    return startA.toMillis() - startB.toMillis();
  });

  return upcoming.length > 0 ? upcoming[0] : null;
};

export const isExceptionCurrentOrUpcoming = (exception: AlarmException, now: Date, timezone: string): boolean => {
  if (!exception.period?.end) {
    return false;
  }

  const nowDateTime = jsDateToDateTime(now).setZone(timezone);
  const endDateTime = dateTimeRecordToLocalisedDateTime(exception.period.end, timezone);

  return endDateTime.toMillis() >= nowDateTime.toMillis();
};

export const prunePastExceptions = (exceptions: AlarmException[], now: Date, timezone: string): AlarmException[] => {
  return exceptions.filter((exception) => isExceptionCurrentOrUpcoming(exception, now, timezone));
};

const exceptionMatches = (a: AlarmException, b: AlarmException): boolean => {
  const pa = a.period;
  const pb = b.period;
  if (!pa?.start || !pa?.end || !pb?.start || !pb?.end) return false;
  return (
    a.id === b.id ||
    (a.action === b.action &&
      a.reason === b.reason &&
      dateTimeRecordEquals(pa.start, pb.start) &&
      dateTimeRecordEquals(pa.end, pb.end))
  );
};

const exceptionContentEquals = (a: AlarmException, b: AlarmException): boolean => {
  const pa = a.period;
  const pb = b.period;
  if (!pa?.start || !pa?.end || !pb?.start || !pb?.end) return false;
  return (
    a.action === b.action &&
    a.reason === b.reason &&
    dateTimeRecordEquals(pa.start, pb.start) &&
    dateTimeRecordEquals(pa.end, pb.end)
  );
};

export const filterNewExceptions = (
  suggested: AlarmException[] | undefined,
  existing: AlarmException[],
): AlarmException[] => {
  if (!suggested || suggested.length === 0) {
    return [];
  }

  return suggested.filter((candidate) => {
    if (!candidate.period?.start || !candidate.period?.end) {
      return false;
    }
    return !existing.some((current) => exceptionMatches(current, candidate));
  });
};

export type ExceptionDisplayItem = {
  type: "added" | "kept" | "removed" | "modified" | "applied";
  exception: AlarmException;
};

const toItem =
  <T extends ExceptionDisplayItem["type"]>(type: T) =>
  (e: AlarmException) =>
    ({ type, exception: e }) as { type: T; exception: AlarmException };

/**
 * Compares current vs agent-suggested exceptions.
 * Add-only → items = added.
 * Remove → items = kept + removed (removed styled destructively).
 * Edit-only → items = modified (edited exceptions shown with suggested version).
 */
export function compareExceptionsForAgentDisplay(
  current: AlarmException[],
  suggested: AlarmException[] | undefined,
): { items: ExceptionDisplayItem[]; hasAdditions: boolean; hasRemovals: boolean } {
  const valid = (e: AlarmException) => e.period?.start && e.period?.end;
  const curr = current.filter(valid);
  const sugg = (suggested ?? []).filter(valid);
  if (sugg.length === 0) return { items: [], hasAdditions: false, hasRemovals: false };

  const inCurr = (c: AlarmException) => curr.some((x) => exceptionMatches(x, c));
  const inSugg = (c: AlarmException) => sugg.some((x) => exceptionMatches(c, x));
  const added = sugg.filter((c) => !inCurr(c));
  const removed = curr.filter((c) => !inSugg(c));
  const kept = sugg.filter(inCurr);

  // Modified: same ID as current but content changed (period, reason, action)
  const modified = kept.filter((k) => {
    const counterpart = curr.find((c) => exceptionMatches(c, k));
    return counterpart != null && !exceptionContentEquals(counterpart, k);
  });

  if (added.length > 0 && removed.length === 0) {
    return { items: added.map(toItem("added")), hasAdditions: true, hasRemovals: false };
  }
  if (removed.length > 0) {
    return {
      items: [...kept.map(toItem("kept")), ...removed.map(toItem("removed")), ...added.map(toItem("added"))],
      hasAdditions: added.length > 0,
      hasRemovals: true,
    };
  }
  if (modified.length > 0) {
    return {
      items: modified.map(toItem("modified")),
      hasAdditions: false,
      hasRemovals: false,
    };
  }
  return { items: [], hasAdditions: false, hasRemovals: false };
}

export const formattedCameraArmedState = (state: CameraArmedState): string => {
  if (state.isInOverride && state.activeOverride) {
    return "Manual Override";
  }

  if (state.isInException && state.activeException) {
    return `Planned Exception: ${state.activeException.reason}`;
  }

  switch (state.armingType) {
    case "schedule_armed":
      return "Armed by schedule";
    case "schedule_disarmed":
      return "Disarmed by schedule";
    case "override_armed":
      return "Manually armed";
    case "override_disarmed":
      return "Manually disarmed";
    case "exception_armed":
      return `Planned arming: ${state.activeException?.reason}`;
    case "exception_disarmed":
      return `Planned disarming: ${state.activeException?.reason}`;
    default:
      return "";
  }
};
